const DPID_TV_KEY = 'dpid_tv_key';

/**
 * Safely reads a value from localStorage.
 * Works in React Native Web and does not throw exceptions
 * if storage is unavailable (private mode, quota exceeded, etc.).
 *
 * @param {string} key - The localStorage key to read.
 * @returns {string | null} The stored value or null if unavailable.
 */
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage?.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Safely writes a value to localStorage.
 * Handles environments where localStorage may be blocked.
 *
 * @param {string} key - The localStorage key to write.
 * @param {string} value - The value to store.
 * @returns {void}
 */
const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage?.setItem(key, value);
  } catch {
    // storage may be unavailable; ignore silently
  }
};

/**
 * Generates a standard UUID (36-char format 8-4-4-4-12).
 * Uses crypto.randomUUID() when available, falling back to a JS implementation.
 *
 * @returns {string} A newly generated UUID string.
 */
const generateUUID = (): string => {
  if (crypto?.randomUUID) return crypto.randomUUID();

  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);

  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

/**
 * Retrieves a persistent Device Persistent ID (DPID) from localStorage.
 *
 * ✔ If a DPID exists → returns it
 * ✔ If not → generates a new UUID, stores it, then returns it
 * ✔ Non-blocking fallback → storage errors do **not** break execution
 *
 * @function getPersistentDPID
 * @returns {string} The persistent DPID for this device/session.
 */
export const getPersistentDPID = (): string => {
  const existingKey = safeGetItem(DPID_TV_KEY);
  if (existingKey) return existingKey;

  const newId = generateUUID();
  safeSetItem(DPID_TV_KEY, newId);

  return newId;
};
